import { PRIVACY_NOTE, pageBySlug, type CatalogueEntry } from '../content/catalogue.js';
import type { PageDetail } from '../content/details.js';
import { DropZone } from './DropZone.js';
import { ImagesToPdf } from './ImagesToPdf.js';
import { Layout } from './Layout.js';
import { ArrowRight, CloudOff } from '../ui/icons.js';
import { AdSlot } from '../ads/AdSlot.js';

/**
 * A tool landing page — plan §12.2.
 *
 * CONTENT FIRST, app second. The indexed URL is a real page: heading and drop
 * zone above the fold, then several hundred words written for this tool, a
 * FAQ, and links to its neighbours. Dropping a file navigates to the editor.
 *
 * Mounting the editor here instead would produce a page whose above-the-fold
 * content is an app UI — thin-content signals, a heavy LCP, no answer to the
 * question the visitor actually searched for, and an AdSense policy risk.
 */

export interface ToolPageProps {
  readonly slug: string;
  /**
   * The page's prose, supplied rather than imported.
   *
   * `details.ts` holds every page's copy and must never reach a
   * client bundle — it was 76% of the entry chunk. On the server the prerender
   * passes it in; in the browser it comes from a JSON block embedded in the
   * page's own HTML. Either way this component never imports it.
   */
  readonly detail: PageDetail | null;
}

/**
 * Structured data, emitted into the prerendered HTML.
 *
 * `HowTo` and `FAQPage` are the two schema types these pages genuinely
 * qualify for; claiming others would be the sort of thing that earns a manual
 * action rather than a rich result.
 */
function structuredData(page: CatalogueEntry, detail: PageDetail): string {
  const graph: unknown[] = [
    {
      '@type': 'FAQPage',
      mainEntity: detail.faq.map((entry) => ({
        '@type': 'Question',
        name: entry.q,
        acceptedAnswer: { '@type': 'Answer', text: entry.a },
      })),
    },
  ];

  if (detail.steps.length > 0) {
    graph.push({
      '@type': 'HowTo',
      name: page.heading,
      description: page.metaDescription,
      step: detail.steps.map((text, i) => ({
        '@type': 'HowToStep',
        position: i + 1,
        text,
      })),
    });
  }

  /**
   * `</script>` inside a JSON string would close the tag early and drop the
   * remainder into the document as markup. Our copy is static today, so this
   * is not exploitable — but the escape is one line, and the alternative is a
   * latent injection that surfaces the first time someone writes a page about
   * embedding scripts in PDFs.
   *
   * `<` and `>` are escaped rather than the literal `</script>` sequence: the
   * `\u` form is transparent to a JSON parser and cannot be reassembled by the
   * HTML one.
   */
  return JSON.stringify({ '@context': 'https://schema.org', '@graph': graph })
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
}

export function ToolPage({ slug, detail }: ToolPageProps) {
  const page = pageBySlug(slug);
  if (!page || !detail) return null;


  return (
    <Layout title={page.metaTitle} description={page.metaDescription} canonical={`/${page.slug}`}>
      <script
        type="application/ld+json"
        // Static, generated from our own content — no user input reaches it.
        dangerouslySetInnerHTML={{ __html: structuredData(page, detail) }}
      />

      {/* Hero — the tool's own claim plus the way in, above the fold. */}
      <div className="relative isolate overflow-hidden border-b border-line">
        <div className="aurora" />
        <div className="shell-narrow relative z-10 pb-14 pt-12 sm:pt-16">
          <nav aria-label="Breadcrumb" className="eyebrow flex items-center gap-2">
            <a href="/" className="transition-colors hover:text-muted">
              Home
            </a>
            <span aria-hidden>/</span>
            <span className="text-muted">{page.title}</span>
          </nav>

          <h1 className="display-sm optical-display mt-4">{page.heading}</h1>
          <p className="prose-lead mt-5">{detail.intro}</p>

          {/**
            * Pages that take IMAGES convert them to a PDF here and hand the
            * result to the editor, so nothing downstream needs to know the
            * document began life as photographs.
            */}
          {page.opener === 'images' ? (
            <ImagesToPdf />
          ) : (
            <DropZone toolId={page.toolId} label={`Select PDF for ${page.title}`} />
          )}
        </div>
      </div>

      <article className="shell-narrow py-14">
        {detail.steps.length > 0 && (
          <section>
            <p className="eyebrow">How it works</p>
            <ol className="mt-5 space-y-0">
              {detail.steps.map((step, i) => (
                <li key={step} className="flex gap-4 border-b border-line py-4 last:border-0">
                  <span className="font-mono-ui mt-0.5 shrink-0 text-[0.6875rem] text-brand">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="leading-relaxed text-muted">{step}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/*
          One in-content placement per tool page — plan §7.3, where the landing
          and content pages are called the revenue backbone because nearly all
          SEO impressions land here.

          Deliberately BELOW the drop zone and the steps, never between the
          heading and the thing the visitor came to do. The slot reserves its
          own height whether or not a network is configured and whether or not
          an ad blocker eats it, so this position costs no layout shift.
        */}
        <div className="mt-12 flex justify-center">
          <AdSlot placement="landing" />
        </div>

        {detail.body.map((section) => (
          <section key={section.heading} className="mt-12">
            <h2 className="headline">{section.heading}</h2>
            <p className="mt-3 leading-relaxed text-muted">{section.text}</p>
          </section>
        ))}

        {/* The privacy note is the same on every page by design, so it is
            styled as a standing notice rather than dressed up as new copy. */}
        <aside className="card mt-12 flex gap-4 p-5">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-line bg-surface-2 text-signal">
            <CloudOff className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-sm font-600">Nothing is uploaded</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-muted">{PRIVACY_NOTE}</p>
          </div>
        </aside>

        {detail.faq.length > 0 && (
          <section className="mt-14">
            <p className="eyebrow">Questions</p>
            <dl className="mt-5">
              {detail.faq.map((entry) => (
                <div key={entry.q} className="border-b border-line py-5 first:pt-0 last:border-0">
                  <dt className="font-550 tracking-tight">{entry.q}</dt>
                  <dd className="mt-2 leading-relaxed text-muted">{entry.a}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {detail.related.length > 0 && (
          <nav className="mt-14">
            <p className="eyebrow">Related tools</p>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {detail.related.map((relatedSlug) => {
                const related = pageBySlug(relatedSlug);
                if (!related) return null;
                return (
                  <li key={relatedSlug}>
                    <a
                      href={`/${related.slug}`}
                      className="card card-link group flex items-center gap-3 p-4"
                    >
                      <span className="font-550 tracking-tight">{related.title}</span>
                      <ArrowRight className="ml-auto h-4 w-4 text-faint transition-colors group-hover:text-brand" />
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
      </article>
    </Layout>
  );
}
